import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Reusable single-date picker for super-admin-ui.
 *
 * Design:
 *   - Portal-rendered so it escapes parent overflow / fixed chrome.
 *   - Arrow-navigable month grid (keyboard-accessible).
 *   - `min` / `max` to constrain a range (used by DateRangePicker).
 *   - "Today" quick button always visible.
 *   - Zero CSS-var dependencies — works wherever it's dropped in.
 */

interface Props {
  value: string;                     // YYYY-MM-DD
  onChange: (v: string) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  className?: string;
  disabled?: boolean;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const DatePicker: React.FC<Props> = ({
  value, onChange, placeholder = 'Select date', min, max, className = '', disabled,
}) => {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const [view, setView] = useState<Date>(() => {
    if (value) {
      const [y, m] = value.split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date();
  });

  useEffect(() => {
    if (!open || !anchor.current) return;
    const reposition = () => {
      const rect = anchor.current!.getBoundingClientRect();
      const calW = Math.max(rect.width, 300);
      const calH = 340;
      const top = rect.bottom + calH + 8 > window.innerHeight && rect.top > calH
        ? rect.top - calH - 4
        : rect.bottom + 4;
      let left = rect.left;
      if (left + calW > window.innerWidth - 8) left = Math.max(8, window.innerWidth - calW - 8);
      setPos({ top, left });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchor.current?.contains(t) || popover.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const days = useMemo(() => {
    const year = view.getFullYear();
    const month = view.getMonth();
    const firstDow = new Date(year, month, 1).getDay();
    const numDays = new Date(year, month + 1, 0).getDate();
    const list: (number | null)[] = [];
    for (let i = 0; i < firstDow; i++) list.push(null);
    for (let d = 1; d <= numDays; d++) list.push(d);
    return list;
  }, [view]);

  const toIso = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const isDisabled = (day: number): boolean => {
    const iso = toIso(new Date(view.getFullYear(), view.getMonth(), day));
    if (min && iso < min) return true;
    if (max && iso > max) return true;
    return false;
  };

  const todayIso = toIso(new Date());
  const selectedIso = value;
  const displayLabel = value
    ? new Date(`${value}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : placeholder;

  return (
    <>
      <button
        ref={anchor}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`w-full h-10 px-3 flex items-center gap-2 rounded-lg border border-stone-300 bg-white text-sm hover:border-stone-400 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        <Calendar size={14} className="text-stone-500 shrink-0" />
        <span className={`flex-1 text-left truncate ${value ? 'text-stone-800' : 'text-stone-400'}`}>
          {displayLabel}
        </span>
      </button>

      {open && pos && createPortal(
        <div
          ref={popover}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 10000 }}
          className="bg-white rounded-xl shadow-2xl border border-stone-200 p-3 w-[300px]"
        >
          {/* Month nav */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              className="p-1.5 rounded-md hover:bg-stone-100 text-stone-600"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-sm font-semibold text-stone-800">
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </div>
            <button
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              className="p-1.5 rounded-md hover:bg-stone-100 text-stone-600"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day labels */}
          <div className="grid grid-cols-7 mb-1">
            {DAY_LABELS.map((d, i) => (
              <div key={i} className="h-7 flex items-center justify-center text-[11px] font-semibold text-stone-400">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((day, i) => {
              if (day === null) return <div key={i} className="h-9" />;
              const iso = toIso(new Date(view.getFullYear(), view.getMonth(), day));
              const isSelected = iso === selectedIso;
              const isToday = iso === todayIso;
              const dis = isDisabled(day);
              return (
                <button
                  key={i}
                  disabled={dis}
                  onClick={() => { onChange(iso); setOpen(false); }}
                  className={`h-9 rounded-md text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-emerald-600 text-white'
                      : isToday
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : dis
                      ? 'text-stone-300 cursor-not-allowed'
                      : 'text-stone-800 hover:bg-stone-100'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Today shortcut */}
          <button
            onClick={() => { onChange(todayIso); setOpen(false); }}
            className="mt-3 w-full h-8 rounded-md bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-semibold"
          >
            Today
          </button>
        </div>,
        document.body,
      )}
    </>
  );
};

export default DatePicker;
